import { redirect } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// Debugging
import debug from 'debug';

// GQL
import { client } from '../../../../../../gql/client';
import { evictDerivedCaches } from '../../../../../../gql/cacheHelpers';
import { DELETE_MANY_IMAGES } from '../../../../../../gql/mutations/accidents';
import { SAVE_ACTION } from '../../../../../../gql/mutations/actions';
import {
  CREATE_NEW_MATERIAL_UNIT,
  DELETE_MANY_MATERIALS,
  UPDATE_MANY_MATERIALS_UNITS,
} from '../../../../../../gql/mutations/materialUnits';
import {
  CREATE_NEW_ANIMAL_UNIT,
  DELETE_MANY_ANIMAL_UNITS,
  UPDATE_MANY_ANIMAL_UNITS,
} from '../../../../../../gql/mutations/animalUnits';
import {
  CREATE_NEW_AUX_MACHINE_UNIT,
  DELETE_MANY_AUX_MACHINE_UNITS,
  UPDATE_MANY_AUX_MACHINE_UNITS,
} from '../../../../../../gql/mutations/auxMachineUnits';
import { NEW_ANIMAL_UNIT } from '../../../../../../gql/fragments/animalUnit';
import { NEW_AUX_MACHINE_UNIT } from '../../../../../../gql/fragments/auxMachineUnit';
import { NEW_MATERIAL_UNIT } from '../../../../../../gql/fragments/materialUnit';
import { GET_IMAGE_BY_ID } from '../../../../../../gql/queries/accidents/accidents';
import {
  CREATE_EXTERNAL_WORK,
  DELETE_EXTERNAL_WORKS_BY_IDS,
  UPDATE_EXTERNAL_WORKS,
} from '../../../../../../gql/mutations/externalWork';

// Constants
import {
  formActionNaming,
  directoryNaming,
} from '../../../../../../components/forms/ActionForms/constants';
import { UNSPECIFIC } from '../../../../../../types/utils';

// Utils
import { decompressSessionStorage } from '../../../../../../utils/sessionStorage';
import {
  UploadImage,
  compareSketchJson,
  CloudinaryUploadError,
} from '../../../../../../utils/image';
import { getClassificationCode } from '../../../../../../utils/orders';
import { saveFilesIntoADirectory } from '../../../../../../utils/files';
import { screenFormContext, type FormContext } from '../../../../../../offline/formStorage';
import {
  addImageUploadOperation,
  OFFLINE_PENDING_UPLOADS_DIR,
} from '../../../../../../offline/offlineImageUploadQueue';
import { getSketchFromSessionStorage } from '../../../../../../utils/sketch';
import i18n from '../../../../../../i18n';

// Types
import { OperatorsSelectionType } from '../../../../../../components/display/tables/formTables/OperatorsSelectionTable/types';
import { GET_ORDER_BY_ID } from '../../../../../../gql/queries/orders/orders';
import { GET_ACTION_BY_ID } from '../../../../../../gql/queries/actions/actions';
import { GET_USER_BY_ID } from '../../../../../../gql/queries/users';
import { GET_ASSET_BY_ID } from '../../../../../../gql/queries/actions/assets';
import { GET_ACTIVITY_BY_ID } from '../../../../../../gql/queries/actions/activitys';
import { OrderState, OrderType } from '../../../../../../types/orders';
import {
  tableIds,
  takeBase,
} from '../../../../../../components/display/tables/types';

// Redux
import { GET_GENERIC_ORDER_BY_ID } from '../../../../../../gql/queries/genericOrders';
import { GET_CAMPAIGN_BY_ID } from '../../../../../../gql/queries/campaign';

import { setCursor } from '../../../../../../state/actions/ui/cursorTables';
import { store } from '../../../../../../state/configureStore';
import { getCursor } from '../../../../../../state/selectors/ui/tables';
import {
  FETCH_EXTERNAL_COMPANIES,
  GET_EXTERNAL_COMPANY_BY_ID,
} from '../../../../../../gql/queries/actions/externalCompanies';
import { GET_MATERIAL_UNIT_BY_ID } from '../../../../../../gql/queries/actions/materials';
import { GET_ANIMAL_UNIT_BY_ID } from '../../../../../../gql/queries/actions/animals';
import { GET_AUX_MACHINE_UNIT_BY_ID } from '../../../../../../gql/queries/actions/auxMachines';
import { AnimalIdTypes, AuxMachineTypes, MaterialIdTypes } from '../../types';
import { GET_EXTERNAL_WORK_BY_ID } from '../../../../../../gql/queries/actions/externalWorks';
import { ADD_BUDGET_ITEM_TO_ACTION } from '../../../../../../gql/mutations/budget/actionBudgetItems';
import { ActionBudgetItemRow } from '../../BudgetItems/types';

// Offline: entrada de la fase 2 y encolado compound (spec 2026-09-08-fase2-encolada)
import { isOnlineNow } from '../../../../../../offline/connectivity';
import { isActuallyOnline } from '../../../../../../offline/checkConnectivity';
import {
  decidePhase2Entry,
  createPhaseDeadline,
  phaseScopedClient,
  raceWithDeadline,
  isTransportFailure,
} from '../../../../../../offline/phase2Transport';
import {
  isOfflineQueuedError,
  createOfflineQueuedError,
} from '../../../../../../offline/isOfflineQueuedError';
import { queueActionPhase2 } from './phase2Queue';
import { enqueuePhotosForUpload } from '../../../../../../utils/enqueuePhotos';
import { planUploads } from '../../../../../../utils/imagePlan';
import {
  isBasicAction,
  omitCrewFieldsForBasicAction,
} from '../../../../../../utils/order';
import { basicScheduleChanges } from '../../../../../../components/forms/ActionForms/ActionEdition/basicSchedule';
import { assertDraftOwner } from '../../../../../../utils/draftOwnership';
import { ACTION_CREATION_SCOPE } from '../../../../../../utils/draftScopes';
import { creationDraftPhotoNames } from '../../creationDraft';
import {
  draftSketchFile,
  draftSketchFileName,
  pickSketchFile,
} from '../../../../../../utils/sketchFile';

// `ctx`: de dónde leer el formulario. Por defecto, la pantalla; el reintento de
// la cola pasa uno en memoria con los datos de la operación y los ids reales, para
// no tocar el formulario abierto (spec outbox-autocontenido v2 § 5).
export const saveData = async (
  opClientOpId?: string,
  ctx: FormContext = screenFormContext,
) => {
  // `opClientOpId` (opcional): id estable de la operación offline (op.id de la
  // cola compound). Cuando el replay offline re-ejecuta esta fase 2, se pasa
  // para que el clientOpId del SAVE_ACTION sea ESTABLE entre replays y el fold
  // de imágenes no se re-aplique. Online (wizard normal) va sin él → UUID fresco
  // que RetryLink congela.
  // Configura namespaces de debug
  const logAction = debug('rois:newaction:save');
  const logOperators = debug('rois:newaction:operators');
  const logVehicles = debug('rois:newaction:vehicles');
  const logExternals = debug('rois:newaction:externals');
  const logSession = debug('rois:newaction:session');
  const logError = debug('rois:newaction:error');

  // Activamos logs en desarrollo
  if (import.meta.env.DEV) {
    debug.enable('rois:newaction:*');
    logAction('🚀 Debug activado para saveData (Nueva Actuación)');
  }
  logAction('🔄 Iniciando proceso de guardado de nueva actuación');

  const orderActionData = JSON.parse(
    decompressSessionStorage(
      ctx.store.getItem(formActionNaming.NEW_ACTION_DATA) as string
    )
  );

  // 🔴 Barrera (invariante I3 del spec 2026-11-02 wizard-draft-ownership): las
  // copias de las pestañas que se van a guardar tienen que ser del borrador
  // en curso. Si una puerta empezó OTRA creación con la pantalla anterior
  // montada (el cronómetro crea la básica B estando en la A, auditoría 2.1 y
  // 2.2), el dueño que dejaron los loaders no es el `draftId` de
  // `new-action-data`: guardar mandaría la ubicación de una sobre la otra. No
  // debería saltar nunca —los loaders descartan las copias ajenas y la
  // pantalla remonta—; si salta, no se escribe NADA (ni se encola). Lanza
  // `DraftOwnerMismatchError` con la etiqueta de Sentry
  // `draft-owner-mismatch:action-creation`. Solo en vivo: el reintento de la
  // cola trabaja con su copia en memoria y no gana rechazos nuevos
  // (`.claude/rules/guard-nuevo-cola-offline.md`). Un borrador sin `draftId`
  // (de antes de este cambio) pasa, como hoy.
  assertDraftOwner(
    ACTION_CREATION_SCOPE,
    orderActionData?.draftId,
    { isReplay: !!opClientOpId },
    ctx.store
  );

  // Actuació bàsica: tripulación y medios son del Recorregut de Vigilància, y
  // este cierre no debe tocarlos. Por DATO (`containerType`, que siembra
  // `createBasicAction`), nunca por la URL — el gating por `pathname` de este
  // wizard es deuda anotada y no se amplía.
  //
  // La vía `parentOrderType` de abajo se conserva por compatibilidad, pero en
  // este flujo nadie la escribe en `NEW_ACTION_DATA`. Desde 2026-09-02 no hace
  // falta: `containerType` lo siembran los DOS caminos de la básica
  // (`createBasicAction` con red, `seedBasicActionOffline` sin ella), así que
  // el camino degradado que la auditoría 2026-08-15 señalaba ya no existe.
  const isBasicActionSubmit = isBasicAction({
    containerType: orderActionData?.containerType,
    parentOrderType: orderActionData?.parentOrderType,
  });

  // ── ENTRADA (spec 2026-09-08-fase2-encolada § 3.4) ──────────────────────
  // La decisión es pura (`decidePhase2Entry`) para poder probarla sin este
  // fichero. Solo se sondea la red cuando la respuesta puede cambiar algo: con
  // ids de la fase 1, sin marca offline, fuera de replay y con la app online.
  const online = isOnlineNow();
  const hasExistingIds = !!(orderActionData?.orderId && orderActionData?.actionId);
  const needsProbe =
    !opClientOpId && !orderActionData?._isOffline && hasExistingIds && online;
  const entry = decidePhase2Entry({
    draftIsOffline: !!orderActionData?._isOffline,
    hasExistingIds,
    isReplay: !!opClientOpId,
    online,
    reachable: needsProbe ? await isActuallyOnline() : online,
  });
  if (entry === 'replay-offline') {
    // GAP 2 (auditoría flap-safety): en replay la Order+Action YA se creó (o ya
    // existía). Re-encolar aquí crearía una op NUEVA → duplicado al reconectar.
    // Lanzamos para que el sync manager conserve la MISMA op y la reintente.
    throw new Error('Replay sin conexión — se reintentará');
  }
  if (entry !== 'online') {
    // `queue-create`: la actuación no existe (fase 1 sin red) → el replay crea.
    // `queue-existing`: ya existe y no llegamos → el replay actualiza por id.
    // Antes este segundo caso encolaba una CREACIÓN y salían dos actuaciones.
    logAction(`📴 fase 2 encolada (${entry})`);
    const { redirectTo } = await queueActionPhase2({
      mode: entry === 'queue-create' ? 'create' : 'existing',
      draft: orderActionData,
    });
    throw createOfflineQueuedError({ redirectTo });
  }

  // ── ONLINE: normal flow (unchanged) ──
  // Toda la sección con red corre bajo UN plazo (spec 2026-09-08-fase2-encolada
  // § 2 D1): un AbortController cuyo signal viaja en cada llamada. Con red
  // degradada suele vencer en las dos lecturas de abajo, ANTES de escribir nada.
  const deadline = createPhaseDeadline();
  const phaseClient = phaseScopedClient(client, deadline);
  // Se marca tras la ÚLTIMA escritura (el encolado de fotos). El catch decide
  // con ella si un fallo de transporte se encola o ya es un éxito.
  let writesCommitted = false;
  try {
    const dispatch = store.dispatch;

    const cursorMultimedia = getCursor(tableIds.MULTIMEDIA)(store.getState());

    if (!orderActionData?.orderId || !orderActionData?.actionId) {
      const errorMsg =
        `orderId o actionId no encontrados en sessionStorage ('new-action-data'). ` +
        `orderId: ${orderActionData?.orderId}, actionId: ${orderActionData?.actionId}`;
      logError(errorMsg);
      Sentry.captureException(new Error(errorMsg));
      throw new Error(errorMsg);
    }

    logSession(
      `📋 Datos básicos de actuación - OrderID: ${orderActionData?.orderId}, ActionID: ${orderActionData?.actionId}`
    );

    const { data } = await phaseClient.query({
      query: GET_ORDER_BY_ID,
      // Lectura fresca (D5): alimenta los diffs; en un replay la caché mentiría.
      fetchPolicy: 'network-only',
      variables: {
        id: orderActionData.orderId,
      },
    });

    const {
      data: { action },
    } = await phaseClient.query({
      query: GET_ACTION_BY_ID,
      // Lectura fresca (D5): alimenta los diffs; en un replay la caché mentiría.
      fetchPolicy: 'network-only',
      variables: {
        // Use the actionId guaranteed by the validation above instead of
        // re-deriving it from data.order.actionIDs[0]: in the offline replay
        // (syncAction → animalsSaveData) GET_ORDER_BY_ID reads the just-created
        // order from the Apollo cache before actionIDs is populated, so
        // actionIDs[0] is undefined → "Variable $id required not provided".
        // Mirrors how lines ~1542/~1604 of this file already resolve the action.
        id: orderActionData.actionId,
      },
    });

    logAction(`📑 Orden obtenida: ${data.order.id}, Actuación: ${action.id}`);

    console.log(
      '📦 [saveData] Leyendo operatorsAndVehiclesData de sessionStorage'
    );
    console.log(
      '📦 [saveData] Key:',
      formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM
    );

    const operatorsRaw = ctx.store.getItem(
      formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM
    );

    console.log('📦 [saveData] operatorsRaw existe:', !!operatorsRaw);
    console.log('📦 [saveData] operatorsRaw length:', operatorsRaw?.length || 0);

    // INTEGRACIÓN 9: Validar si existen datos de operadores (navegación libre)
    let operatorsAndVehiclesData;
    if (operatorsRaw) {
      const operatorsDecompressed = decompressSessionStorage(operatorsRaw);
      console.log(
        '📦 [saveData] operatorsDecompressed length:',
        operatorsDecompressed.length
      );
      console.log(
        '📦 [saveData] operatorsDecompressed first 300 chars:',
        operatorsDecompressed.substring(0, 300)
      );
      operatorsAndVehiclesData = JSON.parse(operatorsDecompressed);
    } else {
      console.log(
        '⚠️ [saveData] No se encontraron datos de operadores - usando valores por defecto'
      );
      operatorsAndVehiclesData = {
        operatorsTable: [],
        vehicleTable: {},
        isExternal: false,
        externals: [],
        totalHours: '00:00',
        hoursType: null,
        registerInitDate: null,
        registerEndDate: null,
      };
    }

    console.log('✅ [saveData] operatorsAndVehiclesData parsed:', {
      keys: Object.keys(operatorsAndVehiclesData),
      operatorsTable: operatorsAndVehiclesData.operatorsTable?.length || 0,
      isExternal: operatorsAndVehiclesData.isExternal,
    });

    logSession('📥 Datos de operadores y vehículos cargados de sessionStorage');

    // 🔍 DEBUG: Inspeccionar datos de vehículos
    logVehicles(
      '🔍 DEBUG: Datos de vehicleTable:',
      JSON.stringify(operatorsAndVehiclesData?.vehicleTable, null, 2)
    );

    // INTEGRACIÓN 9: Validar si existen datos de localización (navegación libre)
    const locationRaw = ctx.store.getItem(
      formActionNaming.NEW_LOCATION_FORM
    );
    let locationData;
    if (locationRaw) {
      locationData = JSON.parse(decompressSessionStorage(locationRaw));
    } else {
      // ✅ FIX Task 5: Usar datos de la primera action del order (padre) como defaults
      console.log(
        '⚠️ [saveData] No se encontraron datos de localización - usando datos del padre'
      );

      // Formatear PK del padre
      const parentPkInit =
        action.pkInitKm !== null && action.pkInitMeter !== null
          ? `${action.pkInitKm}+${String(action.pkInitMeter).padStart(3, '0')}`
          : '0+000';
      const parentPkEnd =
        action.pkEndKm !== null && action.pkEndMeter !== null
          ? `${action.pkEndKm}+${String(action.pkEndMeter).padStart(3, '0')}`
          : '0+000';

      locationData = {
        actionPkInit: parentPkInit,
        actionPkEnd: parentPkEnd,
        actionTrack: action.track || UNSPECIFIC,
        actionDirection: action.direction || UNSPECIFIC,
        actionMargin: action.margin || UNSPECIFIC,
        actionLane: action.lane || UNSPECIFIC,
        actionCoordinates: null, // Las coordenadas GPS se obtienen dinámicamente
        actionRoad: action.roadIDs?.[0] || null,
        actionConcession: action.concessionIDs?.[0] || null,
      };

      logAction('📍 [saveData] Defaults de Location del padre:', locationData);
    }

    console.log('📦 [saveData] Leyendo editionData de sessionStorage');
    console.log('📦 [saveData] Key:', formActionNaming.NEW_EDITION_FORM);

    const editionRaw = ctx.store.getItem(formActionNaming.NEW_EDITION_FORM);

    console.log('📦 [saveData] editionRaw existe:', !!editionRaw);
    console.log('📦 [saveData] editionRaw length:', editionRaw?.length || 0);

    // INTEGRACIÓN 9: Validar si existen datos de edición (navegación libre)
    let editionData;
    if (editionRaw) {
      const editionDecompressed = decompressSessionStorage(editionRaw);
      console.log(
        '📦 [saveData] editionDecompressed length:',
        editionDecompressed.length
      );
      console.log(
        '📦 [saveData] editionDecompressed first 300 chars:',
        editionDecompressed.substring(0, 300)
      );
      editionData = JSON.parse(editionDecompressed);
    } else {
      // ✅ FIX Task 5: Usar datos de la primera action y order del padre como defaults
      console.log(
        '⚠️ [saveData] No se encontraron datos de edición - usando datos del padre'
      );

      // Obtener datos de edition del order padre
      const parentOrder = data.order;

      editionData = {
        editionCapitol: parentOrder.capitolIDs?.[0] || null,
        editionSubCapitol: parentOrder.subCapitolIDs?.[0] || null,
        editionElementOrder: parentOrder.elementOrderIDs?.[0] || null,
        editionActivity: action.activityIDs?.[0] || null,
        editionAsset: action.assetIDs?.[0] || null,
        editionDescription: action.descriptionValidated || null,
        editionLaneCutting: action.laneCutting ? 'true' : 'false',
        editionAssetSwitch: action.assetSwitch ? 'true' : 'false',
        editionSketch: null, // El sketch se maneja aparte
        images: [],
      };

      logAction('📝 [saveData] Defaults de Edition del padre:', editionData);
    }

    console.log('✅ [saveData] editionData parsed:', {
      keys: Object.keys(editionData),
      hasSketch: !!editionData.sketch,
      sketchLength: editionData.sketch?.length || 0,
      hasImages: !!editionData.images,
      imagesLength: editionData.images?.length || 0,
    });

    // INTEGRACIÓN 9: Validar si existen datos de materiales (navegación libre)
    const materialsRaw = ctx.store.getItem(
      formActionNaming.NEW_MATERIALS_FORM
    );
    let materialsData;
    if (materialsRaw) {
      materialsData = JSON.parse(decompressSessionStorage(materialsRaw));
    } else {
      console.log(
        '⚠️ [saveData] No se encontraron datos de materiales - usando valores por defecto'
      );
      materialsData = {
        isMaterialTableDisplayed: 'false',
        materialsTable: {},
      };
    }

    // INTEGRACIÓN 9: Validar si existen datos de animales (navegación libre)
    //
    // Actuació bàsica (2026-08-11): NO registra fauna, y este guardado ni la lee
    // ni la toca. Los dos peligros que cierra, ambos reales:
    //
    //  1. El borrador `NEW_ANIMALS_FORM` SOBREVIVE entre asistentes y hay
    //     entradas al de Actuación que no lo purgan (causa raíz literal de la
    //     «tripulación fantasma»). Sin este guard, una básica creada después de
    //     abandonar una actuación normal con fauna HEREDA esa fauna.
    //  2. El `else` de abajo cae en `isAnimalsTableDisplayed: 'false'`, que más
    //     adelante significa «el usuario las quitó todas» y BORRA las
    //     `AnimalUnit` que la actuación ya tuviera.
    //
    // `'skip'` no entra en ninguna de las dos ramas de abajo ('false' borra,
    // 'true' crea/actualiza): la fauna existente se queda exactamente como está.
    //
    // Spec: docs/superpowers/specs/2026-08-11-basica-sin-fauna-design.md
    const animalsRaw = isBasicActionSubmit
      ? null
      : ctx.store.getItem(formActionNaming.NEW_ANIMALS_FORM);
    let animalsData;
    if (isBasicActionSubmit) {
      animalsData = {
        isAnimalsTableDisplayed: 'skip',
        animalsTable: {},
      };
    } else if (animalsRaw) {
      animalsData = JSON.parse(decompressSessionStorage(animalsRaw));
    } else {
      console.log(
        '⚠️ [saveData] No se encontraron datos de animales - usando valores por defecto'
      );
      animalsData = {
        isAnimalsTableDisplayed: 'false',
        animalsTable: {},
      };
    }

    // INTEGRACIÓN 9: Validar si existen datos de equipamiento (navegación libre)
    // Load Work Equipment Data (vehicles + auxiliary machinery)
    const workEquipmentRaw = ctx.store.getItem(
      formActionNaming.NEW_WORK_EQUIPMENT_FORM
    );
    let workEquipmentData;
    if (workEquipmentRaw) {
      workEquipmentData = JSON.parse(decompressSessionStorage(workEquipmentRaw));
    } else {
      console.log(
        '⚠️ [saveData] No se encontraron datos de equipamiento - usando valores por defecto'
      );
      workEquipmentData = {
        isWorkEquipmentDisplayed: 'false',
        vehiclesTable: {},
        machineryTable: {},
      };
    }

    // Cargar datos de partidas presupuestarias (siguiendo patrón Materials)
    // Para rol Tablet, estos datos no existirán ya que no acceden a la página de partidas
    console.log('📦 [saveData] Leyendo budgetItemsData de sessionStorage');
    console.log('📦 [saveData] Key:', formActionNaming.NEW_BUDGET_ITEMS_FORM);

    const budgetItemsRaw = ctx.store.getItem(
      formActionNaming.NEW_BUDGET_ITEMS_FORM
    );

    console.log('📦 [saveData] budgetItemsRaw existe:', !!budgetItemsRaw);
    console.log(
      '📦 [saveData] budgetItemsRaw length:',
      budgetItemsRaw?.length || 0
    );

    let budgetItemsData;

    // Validar si existen datos de budget items (pueden no existir para rol Tablet)
    if (budgetItemsRaw) {
      const budgetItemsDecompressed = decompressSessionStorage(budgetItemsRaw);
      console.log(
        '📦 [saveData] budgetItemsDecompressed length:',
        budgetItemsDecompressed.length
      );
      console.log(
        '📦 [saveData] budgetItemsDecompressed first 300 chars:',
        budgetItemsDecompressed.substring(0, 300)
      );

      budgetItemsData = JSON.parse(budgetItemsDecompressed);

      console.log('✅ [saveData] budgetItemsData parsed:', {
        keys: Object.keys(budgetItemsData),
        isBudgetItemsTableDisplayed: budgetItemsData.isBudgetItemsTableDisplayed,
        budgetItemsTableKeys: budgetItemsData.budgetItemsTable
          ? Object.keys(budgetItemsData.budgetItemsTable).length
          : 0,
      });
    } else {
      // Crear objeto por defecto cuando no hay datos (rol Tablet)
      console.log(
        '⚠️ [saveData] No se encontraron datos de budget items - usando valores por defecto (rol Tablet)'
      );
      budgetItemsData = {
        isBudgetItemsTableDisplayed: 'false',
        budgetItemsTable: {},
      };
    }

    logAction(`📋 Datos de partidas presupuestarias cargados:`, budgetItemsData);
    logAction(
      `📋 isBudgetItemsTableDisplayed: ${budgetItemsData?.isBudgetItemsTableDisplayed}`
    );
    logAction(
      `📋 budgetItemsTable keys: ${Object.keys(budgetItemsData?.budgetItemsTable || {}).length}`
    );

    if (orderActionData.orderId && orderActionData.actionId) {
      logAction('🔄 Procesando vehículos y conductores');

      const driverAndVehicle: any = Object.values(
        operatorsAndVehiclesData?.vehicleTable
      ).reduce((accumulator: any[], currentDriverAndVehicle: any) => {
        if (
          currentDriverAndVehicle &&
          Object.keys(currentDriverAndVehicle).length > 0 &&
          currentDriverAndVehicle.driverID
        ) {
          // ✅ VALIDACIÓN CRÍTICA: Verificar que vehicleID existe
          if (!currentDriverAndVehicle.vehicleID) {
            logVehicles(
              `⚠️ ADVERTENCIA: Conductor ${currentDriverAndVehicle.driverID} sin vehículo asignado - SALTANDO`
            );
            return accumulator;
          }

          logVehicles(
            `✅ Vehículo válido: Conductor=${currentDriverAndVehicle.driverID}, Vehículo=${currentDriverAndVehicle.vehicleID}`
          );

          accumulator.push({
            vehicleID: currentDriverAndVehicle.vehicleID,
            driverID: currentDriverAndVehicle.driverID,
          });
        }
        return accumulator;
      }, []);

      logVehicles(
        `📊 Vehículos con conductor encontrados: ${driverAndVehicle.length}`
      );

      const operatorsIDs =
        operatorsAndVehiclesData?.operatorsTable?.reduce(
          (accumulator: string[], currentOperator: OperatorsSelectionType) => {
            if (currentOperator) {
              accumulator.push(currentOperator.id);
            }
            return accumulator;
          },
          []
        ) ?? [];

      logOperators(`📊 Operadores: ${operatorsIDs?.length || 0}`);

      const operatorIDs = [...operatorsIDs];

      logOperators(`📊 TOTAL operadores a guardar: ${operatorIDs.length}`);
      logOperators(`📋 IDs de operadores:`, operatorIDs);

      const splitHourAndMinutes =
        operatorsAndVehiclesData.totalHours.split(':');
      const minutesTotals =
        parseInt(splitHourAndMinutes[0]) * 60 +
        parseInt(splitHourAndMinutes[1]);
      const pkInitRaw = locationData.actionPkInit.replace(/ /g, '').split('+');
      const pkEndRaw = locationData.actionPkEnd.replace(/ /g, '').split('+');
      const isExternal = operatorsAndVehiclesData.isExternal;
      const externalCompanies = operatorsAndVehiclesData?.externals;

      logOperators(
        `⚙️ Estado del switch "Actius": ${isExternal ? 'Externos' : 'Internos'}`
      );

      if (externalCompanies) {
        logExternals(`📊 Empresas externas: ${externalCompanies.length || 0}`);
      }

      const externalWorksFetch = await Promise.all(
        action.externalWorksIDs.map(async (externalWorkID: string) => {
          const {
            data: { externalWork },
          } = await phaseClient.query({
            query: GET_EXTERNAL_WORK_BY_ID,
            variables: {
              id: externalWorkID,
            },
          });
          return externalWork;
        })
      );

      logExternals(
        `📊 Trabajos externos existentes: ${externalWorksFetch.length}`
      );

      const externalCompaniesFetch = await Promise.all(
        externalWorksFetch.map(
          async (externalCompanyItem: { externalCompanyIDs: string[] }) => {
            const {
              data: { externalCompany },
            } = await phaseClient.query({
              query: GET_EXTERNAL_COMPANY_BY_ID,
              variables: {
                id: externalCompanyItem.externalCompanyIDs[0],
              },
            });
            return externalCompany;
          }
        )
      );

      logExternals(
        `📊 Empresas externas obtenidas: ${externalCompaniesFetch.length}`
      );

      const externalWorksFetchIDs = externalWorksFetch.map(
        (external) => external.id
      );

      // Ya no se borran external works ni vehicle occupants del "otro lado"
      // Ambos tipos pueden coexistir en la misma actuación

      if (externalCompanies && externalCompanies.length > 0) {
        {
          // Procesar empresas externas si hay datos (independiente de isExternal)
          logExternals('🔄 Procesando actualización de empresas externas');
          const arrayUpdatedExternalWorks: {
            externalWorkId: string;
            operatorsNumber: number;
          }[] = [];

          externalWorksFetch.forEach((externalWork) => {
            externalCompanies.filter(
              (externalCompany: { id: string; operatorsNumber: string }) => {
                if (
                  externalCompany.id === externalWork.externalCompanyIDs[0] &&
                  Number(externalCompany.operatorsNumber) !==
                    externalWork.operatorsNumber
                ) {
                  arrayUpdatedExternalWorks.push({
                    externalWorkId: externalWork.id,
                    operatorsNumber: Number(externalCompany.operatorsNumber),
                  });
                }
              }
            );
          });

          if (arrayUpdatedExternalWorks.length > 0) {
            arrayUpdatedExternalWorks.forEach(async (externalWorkToUpdate) => {
              await phaseClient.mutate({
                mutation: UPDATE_EXTERNAL_WORKS,
                variables: {
                  input: externalWorkToUpdate,
                },
              });
            });
          }

          const newExternalWorks = externalCompanies.filter(
            (externalCompany: { company: string }) => {
              return !externalCompaniesFetch.some(
                (externalCompanyDataFetch) => {
                  return (
                    externalCompany.company ===
                    externalCompanyDataFetch.companyName
                  );
                }
              );
            }
          );

          logExternals(
            `📊 Nuevas empresas externas a crear: ${newExternalWorks.length}`
          );

          const removeExternalCompanies = externalCompaniesFetch?.filter(
            (externalCompanyDataFetch) => {
              return !externalCompanies.some(
                (externalCompany: { company: string }) => {
                  return (
                    externalCompanyDataFetch.companyName ===
                    externalCompany.company
                  );
                }
              );
            }
          );

          logExternals(
            `📊 Empresas externas a eliminar: ${removeExternalCompanies.length}`
          );

          const arrayWorksIDsToDelete: string[] = [];

          removeExternalCompanies.forEach((company) => {
            company.externalWorkIDs.forEach((id: string) => {
              if (externalWorksFetchIDs.includes(id)) {
                arrayWorksIDsToDelete.push(id);
              }
            });
          });

          if (arrayWorksIDsToDelete.length > 0) {
            await phaseClient.mutate({
              mutation: DELETE_EXTERNAL_WORKS_BY_IDS,
              variables: {
                ids: arrayWorksIDsToDelete,
              },
            });
          }

          if (newExternalWorks.length > 0) {
            // Desde 2026-07 el wizard NO da de alta empresas en el catálogo
            // maestro: el desplegable solo ofrece empresas ya existentes y
            // activas, así que TODAS las filas traen un id real. El alta vive
            // en Gestió > Empreses externes (y el backend la deniega a los
            // roles operativos).
            await Promise.all(
              newExternalWorks.map(
                (company: { operatorsNumber: string; id: string }) =>
                  phaseClient.mutate({
                    mutation: CREATE_EXTERNAL_WORK,
                    variables: {
                      input: {
                        operatorsNumber: Number(company.operatorsNumber),
                        externalCompanyId: company.id,
                        actionId: orderActionData.actionId,
                      },
                    },
                  })
              )
            );
          }

          await phaseClient.query({
            query: FETCH_EXTERNAL_COMPANIES,
            fetchPolicy: 'network-only',
          });
        }
      }

      // C-11.10: factories diferidas. ANTES era `Promise<any>[]` y los
      // `phaseClient.mutate()` se invocaban al hacer `push()` → la mutación se
      // disparaba inmediatamente y corría en paralelo con SAVE_ACTION,
      // colisionando en `tx.action.update` (P2034). Ahora cada elemento es
      // una función que sólo invoca la mutation cuando la llamamos en
      // `Promise.all(materialAnimalsAndMachineryCalls.map((fn) => fn()))`,
      // garantizando que todas las CREATE_*_UNIT corran DESPUÉS de SAVE_ACTION.
      const materialAnimalsAndMachineryCalls: Array<() => Promise<any>> = [];

      // Create and remove material, aux machine and animals units
      if (materialsData.isMaterialTableDisplayed === 'false') {
        if (action.materialUnitIDs.length > 0) {
          await phaseClient.mutate({
            mutation: DELETE_MANY_MATERIALS,
            variables: {
              ids: action.materialUnitIDs,
            },
          });
        }
      }
      if (materialsData.isMaterialTableDisplayed === 'true') {
        const materialUnits = await Promise.all(
          action?.materialUnitIDs?.map(async (materialUnitId: string) => {
            const {
              data: { materialUnit },
            } = await phaseClient.query({
              query: GET_MATERIAL_UNIT_BY_ID,
              variables: {
                id: materialUnitId,
              },
            });

            return materialUnit;
          })
        );

        const arrayUpdatedMaterials: {
          materialUnitId: string;
          newValue: number;
        }[] = [];

        materialUnits.forEach((materialUnit) => {
          Object.values<MaterialIdTypes>(materialsData?.materialsTable).filter(
            (material: MaterialIdTypes) => {
              if (
                material.id === materialUnit.materialIDs[0] &&
                Number(material.ut) !== materialUnit.unit
              ) {
                arrayUpdatedMaterials.push({
                  materialUnitId: materialUnit.id,
                  newValue: Number(material?.ut),
                });
              }
            }
          );
        });

        if (arrayUpdatedMaterials.length > 0) {
          await phaseClient.mutate({
            mutation: UPDATE_MANY_MATERIALS_UNITS,
            variables: {
              input: arrayUpdatedMaterials,
            },
          });
        }

        const removeMaterials = materialUnits
          .filter((materialUnit) => {
            return !Object.values<MaterialIdTypes>(
              materialsData.materialsTable
            ).some((materialId: MaterialIdTypes) => {
              return materialId.id.includes(materialUnit.materialIDs);
            });
          })
          .map((material) => material.id);

        if (removeMaterials.length > 0) {
          await phaseClient.mutate({
            mutation: DELETE_MANY_MATERIALS,
            variables: {
              ids: removeMaterials,
            },
          });
        }
        const newMaterials: MaterialIdTypes[] = Object.values<MaterialIdTypes>(
          materialsData?.materialsTable
        ).filter((materialID: MaterialIdTypes) => {
          return !materialUnits.some((materialUnit) => {
            return materialUnit.materialIDs.includes(materialID?.id);
          });
        });
        if (newMaterials.length > 0) {
          for (const materialsDataKey of newMaterials) {
            materialAnimalsAndMachineryCalls.push(() =>
              phaseClient.mutate({
                mutation: CREATE_NEW_MATERIAL_UNIT,
                variables: {
                  input: {
                    unit: Number(materialsDataKey.ut),
                    actionId: orderActionData.actionId,
                    materialId: materialsDataKey.id,
                  },
                },
                update(cache, { data: { createMaterialUnit } }) {
                  cache.modify({
                    fields: {
                      materialUnits(existingMaterialUnits = []) {
                        const newMaterialUnitRef = cache.writeFragment({
                          data: createMaterialUnit,
                          fragment: NEW_MATERIAL_UNIT,
                        });
                        return [...existingMaterialUnits, newMaterialUnitRef];
                      },
                    },
                  });
                },
              })
            );
          }
        }
      }

      if (animalsData.isAnimalsTableDisplayed === 'false') {
        if (action.animalUnitIDs.length > 0) {
          await phaseClient.mutate({
            mutation: DELETE_MANY_ANIMAL_UNITS,
            variables: {
              ids: action.animalUnitIDs,
            },
          });
        }
      }

      if (animalsData.isAnimalsTableDisplayed === 'true') {
        const animalsUnits = await Promise.all(
          action?.animalUnitIDs?.map(async (animalUnitId: string) => {
            const {
              data: { animalUnit },
            } = await phaseClient.query({
              query: GET_ANIMAL_UNIT_BY_ID,
              variables: {
                id: animalUnitId,
              },
            });

            return animalUnit;
          })
        );

        const arrayUpdatedAnimals: {
          animalUnitId: string;
          newValue: number | undefined;
          animalStatusId: string | undefined;
        }[] = [];
        animalsUnits.forEach((animalUnit) => {
          Object.values<AnimalIdTypes>(animalsData?.animalsTable).filter(
            (animal: AnimalIdTypes) => {
              if (
                (animal.id === animalUnit.animalIDs[0] &&
                  Number(animal.ut) !== animalUnit.unit) ||
                (animal.id === animalUnit.animalIDs[0] &&
                  animalUnit.animalStatusIDs[0] !== animal.statusId)
              ) {
                arrayUpdatedAnimals.push({
                  animalUnitId: animalUnit.id,
                  newValue:
                    Number(animal.ut) !== animalUnit.unit
                      ? Number(animal.ut)
                      : undefined,
                  animalStatusId:
                    animalUnit.animalStatusIDs[0] !== animal.statusId
                      ? animal.statusId
                      : undefined,
                });
              }
            }
          );
        });

        if (arrayUpdatedAnimals.length > 0) {
          await phaseClient.mutate({
            mutation: UPDATE_MANY_ANIMAL_UNITS,
            variables: {
              input: arrayUpdatedAnimals,
            },
          });
        }

        const removeAnimals = animalsUnits
          .filter((animalUnit) => {
            return !Object.values<AnimalIdTypes>(
              animalsData?.animalsTable
            ).some((animalId: AnimalIdTypes) => {
              return animalId?.id.includes(animalUnit?.animalIDs);
            });
          })
          .map((animal) => animal.id);

        if (removeAnimals.length > 0) {
          await phaseClient.mutate({
            mutation: DELETE_MANY_ANIMAL_UNITS,
            variables: {
              ids: removeAnimals,
            },
          });
        }

        const newAnimals: AnimalIdTypes[] = Object.values<AnimalIdTypes>(
          animalsData?.animalsTable
        ).filter((animalID: AnimalIdTypes) => {
          return !animalsUnits.some((animalUnit) => {
            return animalUnit?.animalIDs.includes(animalID?.id);
          });
        });
        if (newAnimals.length > 0) {
          for (const animalsDataKey of newAnimals) {
            materialAnimalsAndMachineryCalls.push(() =>
              phaseClient.mutate({
                mutation: CREATE_NEW_ANIMAL_UNIT,
                variables: {
                  input: {
                    unit: Number(animalsDataKey.ut),
                    actionId: orderActionData.actionId,
                    animalId: animalsDataKey.id,
                    animalStatusId: animalsDataKey.statusId,
                  },
                },
                update(cache, { data: { createAnimalUnit } }) {
                  cache.modify({
                    fields: {
                      animalUnits(existingAnimalUnits = []) {
                        const newAnimalUnitRef = cache.writeFragment({
                          data: createAnimalUnit,
                          fragment: NEW_ANIMAL_UNIT,
                        });
                        return [...existingAnimalUnits, newAnimalUnitRef];
                      },
                    },
                  });
                },
              })
            );
          }
        }
      }

      // ========== MARCAR VEHÍCULOS DE OBRA (CONSTRUCTION) ==========
      // ⚠️ NO actualizamos aquí, solo extraemos los IDs para consolidar al final.
      // Los vehículos de obra reflejan el uso real desde "Operarios" y se
      // persisten SIEMPRE, independientemente del switch de Maquinaria Auxiliar
      // (que solo gobierna la visibilidad y la presencia de maquinaria). Por eso
      // NO gateamos por isWorkEquipmentDisplayed: si lo hiciéramos, cerrar la
      // sección perdería el vehículo precargado.
      let workVehicleIds: string[] = [];
      if (workEquipmentData?.vehiclesTable) {
        workVehicleIds = Object.values(workEquipmentData.vehiclesTable)
          .filter((vehicle: any) => vehicle && vehicle.id)
          .map((vehicle: any) => vehicle.id);
        logAction(`🚗 Vehículos de obra marcados: ${workVehicleIds.length}`);
      }

      // ========== MAQUINARIA AUXILIAR ==========
      logAction('🔧 ========== PROCESANDO MAQUINARIA AUXILIAR ==========');
      logAction(`🔧 workEquipmentData:`, workEquipmentData);
      logAction(
        `🔧 isWorkEquipmentDisplayed: ${workEquipmentData?.isWorkEquipmentDisplayed}`
      );
      logAction(
        `🔧 machineryTable keys: ${Object.keys(workEquipmentData?.machineryTable || {}).length}`
      );
      logAction(
        `🔧 action.auxMachineUnitIDs: ${action.auxMachineUnitIDs?.length || 0}`
      );

      // ✅ FIX: El campo correcto es isWorkEquipmentDisplayed, no isMachineryTableDisplayed
      if (workEquipmentData?.isWorkEquipmentDisplayed === 'false') {
        logAction(
          '❌ Equipamiento NO mostrado, eliminando si existen unidades de maquinaria'
        );
        if (action.auxMachineUnitIDs?.length > 0) {
          await phaseClient.mutate({
            mutation: DELETE_MANY_AUX_MACHINE_UNITS,
            variables: {
              ids: action.auxMachineUnitIDs,
            },
          });
          logAction(
            `🗑️ Eliminadas ${action.auxMachineUnitIDs.length} unidades de maquinaria`
          );
        }
      }

      if (
        workEquipmentData?.isWorkEquipmentDisplayed === 'true' &&
        workEquipmentData?.machineryTable
      ) {
        logAction('✅ Equipamiento mostrado, procesando maquinaria...');
        const auxMachineUnits = await Promise.all(
          action?.auxMachineUnitIDs?.map(async (auxMachineUnitId: string) => {
            const {
              data: { auxMachineUnit },
            } = await phaseClient.query({
              query: GET_AUX_MACHINE_UNIT_BY_ID,
              variables: {
                id: auxMachineUnitId,
              },
            });

            return auxMachineUnit;
          })
        );

        const arrayUpdatedAuxMachine: {
          auxMachineUnitId: string;
          newValue: number;
        }[] = [];
        auxMachineUnits.forEach((auxMachineUnit) => {
          Object.values<AuxMachineTypes>(
            workEquipmentData?.machineryTable
          ).filter((auxMachine: AuxMachineTypes) => {
            if (
              auxMachine.id === auxMachineUnit.auxMachineIDs[0] &&
              Number(auxMachine.ut) !== auxMachineUnit.unit
            ) {
              arrayUpdatedAuxMachine.push({
                auxMachineUnitId: auxMachineUnit.id,
                newValue: Number(auxMachine.ut),
              });
            }
          });
        });

        if (arrayUpdatedAuxMachine.length > 0) {
          await phaseClient.mutate({
            mutation: UPDATE_MANY_AUX_MACHINE_UNITS,
            variables: {
              input: arrayUpdatedAuxMachine,
            },
          });
        }

        const removeAuxMachineUnits = auxMachineUnits
          .filter((auxMachineUnit) => {
            return !Object.values<AuxMachineTypes>(
              workEquipmentData?.machineryTable
            ).some((auxMachineId: AuxMachineTypes) => {
              return auxMachineId?.id.includes(auxMachineUnit?.auxMachineIDs);
            });
          })
          .map((auxMachine) => auxMachine.id);

        if (removeAuxMachineUnits.length > 0) {
          await phaseClient.mutate({
            mutation: DELETE_MANY_AUX_MACHINE_UNITS,
            variables: {
              ids: removeAuxMachineUnits,
            },
          });
        }

        const newAuxMachine: AuxMachineTypes[] = Object.values<AuxMachineTypes>(
          workEquipmentData?.machineryTable
        ).filter((auxMachineId: AuxMachineTypes) => {
          return !auxMachineUnits.some((auxMachineUnit) => {
            return auxMachineUnit?.auxMachineIDs.includes(auxMachineId?.id);
          });
        });

        logAction(`🆕 Maquinaria NUEVA a crear: ${newAuxMachine.length}`);
        if (newAuxMachine.length > 0) {
          logAction(`🔧 Detalles de maquinaria nueva:`);
          newAuxMachine.forEach((m, idx) => {
            logAction(`   [${idx + 1}] ID: ${m.id}, ut: ${m.ut}`);
          });

          for (const machineryDataKey of newAuxMachine) {
            logAction(
              `🔧 Añadiendo CREATE_NEW_AUX_MACHINE_UNIT para: ${machineryDataKey.id}`
            );
            materialAnimalsAndMachineryCalls.push(() =>
              phaseClient.mutate({
                mutation: CREATE_NEW_AUX_MACHINE_UNIT,
                variables: {
                  input: {
                    unit: Number(machineryDataKey.ut),
                    actionId: orderActionData.actionId,
                    auxMachineId: machineryDataKey.id,
                  },
                },
                update(cache, { data: { createAuxMachineUnit } }) {
                  cache.modify({
                    fields: {
                      auxMachineUnits(existingAuxMachineUnits = []) {
                        const newAuxMachineRef = cache.writeFragment({
                          data: createAuxMachineUnit,
                          fragment: NEW_AUX_MACHINE_UNIT,
                        });
                        return [...existingAuxMachineUnits, newAuxMachineRef];
                      },
                    },
                  });
                },
              })
            );
          }
        }
      }
      let geoPositionPlace;
      if (locationData?.actionCoordinates) {
        const actionCoordinatesParse = JSON.parse(
          locationData?.actionCoordinates
        );
        geoPositionPlace = {
          latitude: actionCoordinatesParse.lat,
          longitude: actionCoordinatesParse.lng,
        };
      }
      // C-11.10 (2026-05-06) — fix P2034 deadlock:
      //
      // El SAVE_ACTION del backend (`saveActionUpdate`) ejecuta `tx.order.update`
      // con `set: [], connect: { id }` para concession, road, capitol, subCapitol,
      // classification, predefinedOrder, teams. En MongoDB+Prisma cada `connect`
      // adquiere un lock en el documento del extremo opuesto (Concession.orderIDs,
      // Road.orderIDs, etc.).
      //
      // CREATE_POSITION_ACTION ejecuta `geoPositionAction.create({ data: {
      // roads: { connect }, concessions: { connect }, actions: { connect } } })`,
      // que también bloquea Road y Concession.
      //
      // Cuando ambas mutations corrían en `Promise.all`, los locks se solapaban
      // → deadlock P2034. Antes (C-5.2 main legacy) el flag `skipOrderSync: true`
      // evitaba el problema porque la cascada Order no se ejecutaba. Al migrar a
      // SAVE_ACTION ese flag desapareció — y olvidamos serializar.
      //
      // Solución: SAVE_ACTION primero (atómico, completa los locks de Order y
      // su Tipo B antes de soltar la transacción). Después, en paralelo, todo
      // lo demás: CREATE_POSITION_ACTION + sub-units (material/animal/aux), que
      // sólo escriben en Action, GeoPositionAction y los nodos hoja.
      // C-11.12: SAVE_ACTION ahora cubre todos los campos Tipo B + Tipo C de
      // Action y Order, incluidos los que faltaban: `activityId`, `assetId` y
      // `elementOrderId`. Antes esos 3 viajaban por UPDATE_ACTION_RELATIONSHIPS
      // post-Save (los 2 primeros) o se perdían (`elementOrder` no estaba ni
      // en el schema). Tras añadirlos al SaveActionInput backend (C-11.12),
      // el front consolida aquí toda la persistencia denormalizada Order↔Action.
      //
      // FOLD (B2, At.0001979.0008.02): la CREACIÓN de imágenes se pliega DENTRO
      // de saveAction (input.images). Por eso computamos aquí — ANTES del
      // saveAction — la subida a Hetzner y el set de imágenes nuevas. El
      // try/catch offline de UploadImage queda INTACTO (fallback de encolado).
      const orderImages = await Promise.all(
        data.order.imageIDs.map(async (imageItem: string) => {
          const {
            data: { image },
          } = await phaseClient.query({
            query: GET_IMAGE_BY_ID,
            variables: {
              id: imageItem,
            },
          });

          return image;
        })
      );

      // Sketch comparison - for new actions, original sketch is always null
      const editionSketch = editionData?.editionSketch ?? null;

      logAction(
        `🖼️ editionSketch en session:`,
        editionSketch ? 'SÍ (con datos)' : 'NO (null)'
      );

      const { sketchToSaveOrUpdate } = compareSketchJson(editionSketch, null);
      // 🔴 El fichero de SU croquis, por nombre (spec 2026-11-02
      // wizard-draft-ownership § 6; minor diferido de la Task 5): la carpeta
      // `canvas-action` la comparten las pestañas y crear y validar actuación,
      // y se subía «el primero de la carpeta» aunque fuera el croquis de otra
      // actuación. Solo si el borrador tiene croquis (`sketchToSaveOrUpdate`).
      const ownSketchFile = sketchToSaveOrUpdate
        ? await draftSketchFileName(
            draftSketchFile(editionSketch),
            ctx.images,
            directoryNaming.CANVAS
          )
        : null;

      logAction(`🖼️ sketchToSaveOrUpdate:`, sketchToSaveOrUpdate ? 'SÍ' : 'NO');

      const newOrderMetadata = {
        indexExpedition: data.order.indexExpedition,
        indexType: data.order.indexType || 0,
        classification: data.order.classificationIDs?.[0]
          ? await getClassificationCode(data.order.classificationIDs[0])
          : '01',
        orderType: OrderType.Action,
      };

      // ── PLAN DE FOTOS (2026-08 / 2026-09) ───────────────────────────────
      // Las fotos no bloquean el guardado: se encolan y suben en segundo plano.
      // Aquí solo se PLANIFICAN (`photoPlan` es la intención del usuario y
      // alimenta `removeImages` más abajo; no vale `selectedImages`, que solo
      // trae el croquis). El encolado real es la ÚLTIMA escritura de la fase
      // (spec 2026-09-08-fase2-encolada § 2 D4): si el plazo vence antes, el
      // snapshot de la cola se lleva las fotos de OPFS; si vence después, todo
      // está escrito y no hay replay. Y como va detrás del saveAction, ya no
      // puede chocar con su transacción (P2034, medido 2026-09-04): la
      // retención de la fila (`holdOrderUploads`) deja de hacer falta aquí.
      //
      // 🔴 Solo las fotos que LISTA el borrador (`editionImages` de «Datos
      // Act»), nunca «todo lo que haya en la carpeta» (spec 2026-11-02 § 6):
      // la carpeta `action` de OPFS la comparten las pestañas y «Datos Act» de
      // validación la llena con las fotos de la actuación que valida; sin la
      // lista, este guardado se las llevaba a la actuación nueva.
      const photoPlan = await planUploads(OrderType.Action, newOrderMetadata, {
        images: ctx.images,
        onlyNames: creationDraftPhotoNames(editionData),
      });
      logAction(`📦 fotos planificadas para subida diferida: ${photoPlan.length}`);

      // El croquis SÍ sigue siendo bloqueante: es uno solo, pesa poco y su
      // rescate offline ya está probado.
      let selectedImages: any[] = [];
      try {
        // No pasa por Apollo: el plazo lo aplica raceWithDeadline (AbortError al vencer).
        selectedImages = await raceWithDeadline(
          UploadImage(
            undefined,
            ownSketchFile ? directoryNaming.CANVAS : undefined,
            newOrderMetadata,
            {
              throwOnError: true,
              orderId: orderActionData.orderId,
              entityType: 'action',
              formImages: ctx.images,
              formStore: ctx.store,
              canvasFileName: ownSketchFile,
            }
          ),
          deadline
        );
      } catch (uploadErr) {
        // Rescate-en-replay (bug cazado en pruebas manuales 2026-07-04, flap
        // a mitad del replay del CREAR): en replay (opClientOpId presente)
        // las imágenes YA viven en op.localImages y se restauran a OPFS en
        // cada reintento — encolar aquí el croquis en offlineImageUploadQueue
        // lo DUPLICA (una vez vía esa cola, otra vía el fold de imágenes del
        // saveAction fase-2 de un intento posterior). Peor: cuando ese
        // intento posterior llega, su clientOpId estable
        // (`${opClientOpId}-action-phase2`) ya se consumió en un intento
        // previo → el backend dedupea y la FOTO normal se pierde. En replay
        // no rescatamos — dejamos que el error se propague al catch externo,
        // que ya relanza (GAP 1) y conserva la op compound: el siguiente
        // reintento re-sube TODO y converge todo-o-nada.
        if (
          !opClientOpId &&
          uploadErr instanceof CloudinaryUploadError &&
          sketchToSaveOrUpdate
        ) {
          try {
            let canvasFiles: { file: File; id: string }[] = [];
            try {
              canvasFiles = await ctx.images.list(directoryNaming.CANVAS);
            } catch {
              canvasFiles = [];
            }
            // El de este borrador, por nombre (nunca «el primero»).
            const sourceFile = ownSketchFile
              ? pickSketchFile(canvasFiles, ownSketchFile)?.file
              : undefined;
            logAction(
              '🌐 Cloudinary failed offline — encolando sketch (sourceFile=%s)',
              sourceFile ? `${sourceFile.name} ${sourceFile.size}B` : 'NONE'
            );
            if (sourceFile) {
              const blobFileName = `pending_sketch_${orderActionData.orderId}_${Date.now()}.png`;
              // Directorio dedicado — sobrevive al cleanup post-save
              await saveFilesIntoADirectory(OFFLINE_PENDING_UPLOADS_DIR, [
                new File([sourceFile], blobFileName, {
                  type: sourceFile.type || 'image/png',
                }),
              ]);
              const sketchData = getSketchFromSessionStorage('action', ctx.store);
              const queuedId = await addImageUploadOperation({
                kind: 'sketch',
                orderId: orderActionData.orderId,
                orderType: OrderType.Action,
                blobDirectory: OFFLINE_PENDING_UPLOADS_DIR,
                blobFileName,
                orderMetadata: newOrderMetadata,
                sketchData: {
                  svgContent: sketchData.svgContent,
                  fabricJson: sketchData.fabricJson,
                },
                // Sin sketchToRemoveId — es creación, no edición
              });
              logAction(
                '📦 sketch encolado offline (NewAction) id=%s blob=%s/%s',
                queuedId,
                OFFLINE_PENDING_UPLOADS_DIR,
                blobFileName
              );
              window.dispatchEvent(
                new CustomEvent('offline-sync-error', {
                  detail: { message: i18n.t('errors:sketchPendingUpload') },
                })
              );
            }
          } catch (queueErr) {
            logAction('⚠️ Error encolando sketch offline: %O', queueErr);
          }
        } else {
          throw uploadErr;
        }
      }

      logAction(`📸 selectedImages count: ${selectedImages.length}`);
      logAction(
        `📸 selectedImages:`,
        selectedImages.map((img) => img.name)
      );

      const newImages = selectedImages.filter((selectedImage) => {
        return !orderImages.some((orderImage) => {
          return selectedImage.name === orderImage.name;
        });
      });

      await phaseClient.mutate({
        mutation: SAVE_ACTION,
        variables: {
          // Actuació bàsica: este cierre mandaba `userIds: []` y `vehicles: []`
          // —el formulario de operarios no existe en su wizard—, y el backend
          // traduce eso a «desconecta a todos» + «borra los VehicleOccupant».
          // Así es como la básica perdía al cerrarse la tripulación que había
          // heredado al nacer. Ahora ni la hereda ni se pide borrarla.
          input: omitCrewFieldsForBasicAction({
            // Idempotencia: UUID estable por operación lógica (RetryLink
            // reintenta con las mismas variables → mismo clientOpId → el backend
            // no duplica si el saveAction ya se aplicó). En replay offline se
            // deriva de op.id para que el fold de imágenes tampoco se re-aplique.
            clientOpId: opClientOpId
              ? `${opClientOpId}-action-phase2`
              : crypto.randomUUID(),
            id: orderActionData.actionId,
            state: OrderState.Open,
            // Actuació bàsica: sin pestaña Operarios, su horario sale de
            // «Datos Act» y SOLO lo que el usuario cambió (12-oct): lo no
            // tocado no viaja y no pisa lo que el back estampe. También en el
            // reintento encolado, que lee el mismo formulario.
            ...(isBasicActionSubmit
              ? basicScheduleChanges(editionData)
              : {
                  registerInitDate: operatorsAndVehiclesData.registerInitDate,
                  registerEndDate: operatorsAndVehiclesData.registerEndDate
                    ? operatorsAndVehiclesData.registerEndDate
                    : undefined,
                  // Hora de llegada (opcional). No afecta al cálculo de minutos.
                  registerArrivalDate:
                    operatorsAndVehiclesData.registerArrivalDate
                      ? operatorsAndVehiclesData.registerArrivalDate
                      : undefined,
                }),
            pkInitKm: pkInitRaw ? Number(pkInitRaw[0]) : undefined,
            pkInitMeter: pkInitRaw ? Number(pkInitRaw[1]) : undefined,
            pkEndKm: pkEndRaw ? Number(pkEndRaw[0]) : undefined,
            pkEndMeter: pkEndRaw ? Number(pkEndRaw[1]) : undefined,
            track: locationData.actionTrack,
            direction: locationData.actionDirection ?? null,
            margin: locationData.actionMargin ?? null,
            lane: locationData.actionLane,
            concessionId: locationData.actionConcession
              ? locationData.actionConcession
              : undefined,
            roadId: locationData.actionRoad
              ? locationData.actionRoad
              : undefined,
            capitolId: editionData.editionCapitol,
            subCapitolId: editionData.editionSubCapitol,
            predefinedOrderId: editionData.editionPredefine,
            elementOrderId: editionData.editionElementOrder || undefined,
            activityId: editionData.editionActivity || undefined,
            assetId: editionData.editionAsset || undefined,
            typeOfHour: operatorsAndVehiclesData.hoursType,
            minutesTotals,
            descriptionValidated: editionData.editionDescription,
            // Operario que registra, elegido en Datos Act. La creación lo sembraba
            // (paso 1 en la normal, el usuario de la tablet en la básica) y esta
            // fase 2 nunca lo enviaba, así que lo elegido aquí se descartaba en
            // silencio — en la básica no hay otro sitio donde ponerlo.
            registeredByUserId: editionData.registeredByUserId || undefined,
            laneCutting:
              editionData.editionLaneCutting &&
              editionData.editionLaneCutting === 'true',
            assetSwitch: editionData.editionAssetSwitch === 'true',
            isExternal,
            // Actuación simultánea (merge main #46): el backend deriva las horas
            // a 0 al reconciliar si está marcada.
            isSimultaneous: operatorsAndVehiclesData.isSimultaneous ?? false,
            // ── FOLD (B1, At.0001979.0008.02): operarios + vehículos (coche+
            // conductor) + workVehicles + geoPosición consolidados DENTRO de
            // saveAction (atómico). Antes iban por NEW_VEHICLE_OCCUPANT_ACTION +
            // CREATE_POSITION_ACTION + updateActionRelationships (cadena no-atómica
            // que dejó la actuación a medias). auxMachines siguen por
            // updateActionRelationships porque sus units se crean DESPUÉS.
            userIds: operatorIDs,
            vehicles: driverAndVehicle.map(
              (dv: { driverID: string; vehicleID: string }) => ({
                vehicleId: dv.vehicleID,
                driverId: dv.driverID,
              })
            ),
            workVehicleIds,
            geoPosition:
              locationData.actionRoad && locationData.actionConcession
                ? {
                    pkInitKm: pkInitRaw ? Number(pkInitRaw[0]) : 0,
                    pkInitMeter: pkInitRaw ? Number(pkInitRaw[1]) : 0,
                    pkEndKm: pkEndRaw ? Number(pkEndRaw[0]) : 0,
                    pkEndMeter: pkEndRaw ? Number(pkEndRaw[1]) : 0,
                    track: locationData.actionTrack,
                    direction: locationData?.actionDirection ?? UNSPECIFIC,
                    margin: locationData?.actionMargin ?? UNSPECIFIC,
                    lane: locationData.actionLane,
                    latitude: geoPositionPlace?.latitude,
                    longitude: geoPositionPlace?.longitude,
                    roadId: locationData.actionRoad,
                    concessionId: locationData.actionConcession,
                  }
                : undefined,
            // ── FOLD (B2, At.0001979.0008.02): CREACIÓN de imágenes plegada
            // DENTRO de saveAction (el backend las crea en su $transaction).
            // Pasamos SOLO `newImages` (las que NO están ya en orderImages),
            // así un re-guardado no duplica. La rama offline (fallo de subida)
            // queda igual: encola y sincroniza aparte. El borrado (removeImages
            // / DELETE_MANY_IMAGES) NO se pliega — sigue después del saveAction.
            images: newImages
              .filter((img) => !!img.storageKey)
              .map((img) => ({
                storageKey: img.storageKey,
                name: img.name,
                creatingDate: img.creatingDate,
                sketchSvg: img.sketchSvg ?? null,
                sketchFabricJson: img.sketchFabricJson ?? null,
              })),
          }, isBasicActionSubmit),
        },
      });
      // FOLD (B1): CREATE_POSITION_ACTION y el loop NEW_VEHICLE_OCCUPANT_ACTION
      // ELIMINADOS — la geoPosición y los VehicleOccupant ahora los crea
      // saveAction (input.geoPosition / input.vehicles) atómicamente. Aquí solo
      // quedan las units (material/animal/auxMachine), que se conectan luego.
      await Promise.all(materialAnimalsAndMachineryCalls.map((fn) => fn()));

      // FOLD (B2, At.0001979.0008.02): la CREACIÓN online de imágenes ya la hace
      // saveAction (input.images) atómicamente — el loop CREATE_NEW_IMAGE fue
      // ELIMINADO. `orderImages`, `selectedImages` y `newImages` se computan
      // ARRIBA, antes del saveAction. Aquí sólo queda el BORRADO de imágenes
      // huérfanas (removeImages), que NO es parte de la cadena del incidente.
      // 🔴 Se compara contra la INTENCIÓN (croquis subido + fotos
      // planificadas), NO contra lo que se subió. Con el outbox las fotos se
      // encolan, así que `selectedImages` solo trae el croquis: comparar solo
      // con eso marcaría para borrar TODAS las fotos que la orden ya tuviera
      // (reintento tras fallo parcial, re-guardado, replay de la compound).
      const intendedImageNames = new Set<string>([
        ...selectedImages.map((selectedImage) => selectedImage.name),
        ...photoPlan.map((planned) => planned.name),
      ]);
      const removeImages = orderImages
        .filter((orderImage) => !intendedImageNames.has(orderImage.name))
        .map((image) => image.id);

      if (removeImages.length > 0) {
        await phaseClient.mutate({
          mutation: DELETE_MANY_IMAGES,
          variables: {
            ids: removeImages,
          },
        });
      }

      logAction(`📸 newImages creadas vía saveAction: ${newImages.length}`);
      logAction(`📸 removeImages a eliminar: ${removeImages.length}`);

      // 📋 PROCESAR PARTIDAS PRESUPUESTARIAS (SOLO CREAR NUEVAS - PATRÓN MATERIALS)
      if (budgetItemsData.isBudgetItemsTableDisplayed === 'true') {
        const budgetItems = Object.values(
          budgetItemsData.budgetItemsTable || {}
        ) as ActionBudgetItemRow[];

        if (budgetItems.length > 0) {
          logAction(
            `🔄 Creando ${budgetItems.length} nuevas partidas presupuestarias`
          );

          // CRÍTICO: try/catch DENTRO del for, NO fuera. Si lo envolvemos
          // alrededor del loop completo, la primera mutation que se encole
          // offline (rechaza con `offlineQueued: true`) interrumpe el for y
          // los items 2..N nunca se procesan. Cada mutate debe ser independiente
          // para que OfflineQueueLink encole cada partida por separado.
          let queuedOffline = 0;
          for (const item of budgetItems) {
            logAction(
              `🔄 Creando partida: ${item.code} - Cantidad: ${item.quantity}`
            );
            try {
              await phaseClient.mutate({
                mutation: ADD_BUDGET_ITEM_TO_ACTION,
                variables: {
                  actionId: orderActionData.actionId,
                  budgetItemId: item.budgetItemId,
                  quantity: item.quantity,
                },
              });
              logAction(
                `✅ Partida creada: ${item.code} - Cantidad: ${item.quantity}`
              );
            } catch (error) {
              if (isOfflineQueuedError(error)) {
                queuedOffline++;
                logAction(
                  `📦 ADD_BUDGET_ITEM_TO_ACTION (item ${item.code}) encolada offline — continuando con siguiente`
                );
              } else {
                // Paridad GAP 1 — en replay no tragar, para que la op se
                // conserve y reintente (espejo de
                // ActionsValidation/Animals/Action/saveData.ts, catches
                // per-item de budget).
                if (opClientOpId || isTransportFailure(error)) throw error;
                logError(`❌ Error creando partida ${item.code}: %O`, error);
                Sentry.captureException(error);
                // No re-lanzamos: mantenemos la semántica original (catch swallow)
                // para no interrumpir el flujo. Los items siguientes sí se procesarán.
              }
            }
          }
          if (queuedOffline > 0) {
            logAction(
              `📦 ${queuedOffline}/${budgetItems.length} partidas encoladas offline`
            );
          } else {
            logAction(
              `✅ Todas las partidas presupuestarias creadas exitosamente`
            );
          }
        } else {
          logAction('ℹ️ No hay partidas presupuestarias para crear');
        }
      } else {
        logAction('ℹ️ Partidas presupuestarias deshabilitadas');
      }

      // Fotos al final (spec 2026-09-08-fase2-encolada § 2 D4): ÚLTIMA escritura
      // de la fase. Antes del plazo → el snapshot de la cola se las lleva;
      // después → no hay replay. Sin contabilidad de "qué fotos fueron ya".
      await enqueuePhotosForUpload({
        picturesDirectoryName: OrderType.Action,
        orderId: orderActionData.orderId,
        orderType: OrderType.Action,
        entityType: 'action',
        orderMetadata: newOrderMetadata,
        plan: photoPlan,
        images: ctx.images,
        handoff: ctx.photoHandoff,
      });
      writesCommitted = true;

      // QA S3 / T3.4 (15-oct): todo lo que sigue son RELECTURAS para la
      // caché; lo escrito ya está escrito. Se esperaban en serie (tres tandas)
      // antes de salir, y con el back lento el velo de carga se quedaba más
      // de 30 s con la básica ya guardada. Se lanzan detrás: el resumen pide
      // lo suyo al servidor (`selectActionById`, network-only). Un fallo aquí
      // solo deja la caché como estaba, y ya no tumba un guardado hecho.
      const refreshCachesAfterSave = async () => {
        // ========== CONSOLIDAR TODAS LAS RELACIONES ==========
        logAction(`🔄 ========== CONSOLIDANDO RELACIONES ==========`);
        logAction(
          `🔄 materialAnimalsAndMachineryCalls ejecutadas: ${materialAnimalsAndMachineryCalls.length}`
        );

        // Recargar action para obtener auxMachineUnitIDs recién creados
        logAction(`🔄 Recargando action para obtener IDs de unidades creadas...`);
        const {
          data: { action: actionWithUnits },
        } = await phaseClient.query({
          query: GET_ACTION_BY_ID,
          variables: { id: orderActionData.actionId },
          fetchPolicy: 'network-only',
        });

        logAction(`✅ Action recargada exitosamente`);
        logAction(`👥 operatorIDs: ${operatorIDs.length}`);
        logAction(
          `🚗 vehicleOccupants (fold saveAction): ${driverAndVehicle.length}`
        );
        logAction(`🚛 workVehicleIDs: ${workVehicleIds.length}`);
        logAction(
          `🔧 auxMachineUnitIDs en action recargada: ${actionWithUnits.auxMachineUnitIDs?.length || 0}`
        );
        if (actionWithUnits.auxMachineUnitIDs?.length > 0) {
          logAction(
            `🔧 IDs de maquinaria: ${actionWithUnits.auxMachineUnitIDs.join(', ')}`
          );
        }

        // FOLD (B-aux): UPDATE_ACTION_RELATIONSHIPS ELIMINADO por completo.
        // operators/vehicleOccupants/workVehicles/geoPosición ya los persiste
        // saveAction atómicamente. Los auxMachineUnits se auto-conectan a la Action
        // al crearse (auxMachineUnit.service.create → `actions: { connect }`, vía
        // CREATE_NEW_AUX_MACHINE_UNIT con actionId) y los stale se borran arriba
        // (DELETE_MANY_AUX_MACHINE_UNITS), así que el `set` de updateRelationships
        // era redundante. NewAction ya NO usa la cadena no-atómica del incidente.

        logAction(
          `✅ Relaciones consolidadas (saveAction atómico + auto-connect units)`
        );

        await Promise.all([
          ...operatorIDs.map((operatorId: string) =>
            phaseClient.query({
              fetchPolicy: 'network-only',
              query: GET_USER_BY_ID,
              variables: {
                input: {
                  id: operatorId,
                },
              },
            })
          ),
          phaseClient.query({
            fetchPolicy: 'network-only',
            query: GET_ACTIVITY_BY_ID,
            variables: { id: editionData.editionActivity },
          }),
          phaseClient.query({
            fetchPolicy: 'network-only',
            query: GET_ACTION_BY_ID,
            variables: { id: orderActionData.actionId },
          }),
          phaseClient.query({
            fetchPolicy: 'network-only',
            query: GET_ORDER_BY_ID,
            variables: { id: orderActionData.orderId },
          }),
        ]);
        if (editionData?.editionAsset) {
          await phaseClient.query({
            fetchPolicy: 'network-only',
            query: GET_ASSET_BY_ID,
            variables: { id: editionData.editionAsset },
          });
        }

        const {
          data: { order },
        } = await phaseClient.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: orderActionData.orderParentId,
          },
        });

        // Refresh de la entidad del padre POR TIPO (2026-07-21): antes era un
        // ternario binario Generic-o-Campaign y un padre RV caía en
        // GetCampaignById con id undefined (error visible al cerrar la básica).
        // Para el RV no hay entidad que refrescar aquí: la card de Órdenes
        // asociadas lee de la Order padre (refetcheada justo arriba) y el RV
        // no cambia al cerrar una actuación.
        const parentRefreshQueries = [
          phaseClient.query({
            fetchPolicy: 'network-only',
            query: GET_ORDER_BY_ID,
            variables: {
              id: orderActionData.orderParentId,
            },
          }),
        ];
        if (order.type === OrderType.Generic && order.genericOrdersIDs?.[0]) {
          parentRefreshQueries.push(
            phaseClient.query({
              fetchPolicy: 'network-only',
              query: GET_GENERIC_ORDER_BY_ID,
              variables: { id: order.genericOrdersIDs[0] },
            })
          );
        } else if (
          order.type === OrderType.Campaign &&
          order.campaignIDs?.[0]
        ) {
          parentRefreshQueries.push(
            phaseClient.query({
              fetchPolicy: 'network-only',
              query: GET_CAMPAIGN_BY_ID,
              variables: { id: order.campaignIDs[0] },
            })
          );
        }
        await Promise.all(parentRefreshQueries);
      };
      refreshCachesAfterSave().catch((refreshErr) => {
        logAction('ℹ️ relecturas tras guardar fallidas (solo caché)');
        console.warn('[NewAction] relecturas tras guardar:', refreshErr);
      });
    }

  } catch (error) {
    logError('❌ Error en el proceso de guardado:', error);
    if (opClientOpId) {
      // GAP 1 (auditoría flap-safety): en replay (opClientOpId = op.id de la
      // cola compound) tragar este error dejaría que el sync manager borrara
      // la op sin que los satélites se hubieran guardado. Relanzar SIEMPRE:
      // la cola lo clasifica y conserva la MISMA op para reintentar.
      Sentry.captureException(error);
      throw error;
    }
    if (!isTransportFailure(error)) {
      // Negocio (400, validación del backend…): antes se tragaba y la función
      // seguía hasta el redirect al resumen como si hubiera guardado (spec
      // 2026-09-08-fase2-encolada § 0.2.a). Ahora sube al router action, que
      // avisa con el mensaje del backend; el asistente se queda.
      Sentry.captureException(error);
      throw error;
    }
    if (!writesCommitted) {
      // Red lenta o cortada con la actuación ya creada: la fase 2 entera a la
      // cola contra los ids (D1/D2). El replay converge: update por id +
      // diffs con lecturas frescas + dedupe del backend por clave natural.
      console.warn(
        '[offline] fase 2 sin llegar al servidor; encolada contra los ids',
        error
      );
      const { redirectTo } = await queueActionPhase2({
        mode: 'existing',
        draft: orderActionData,
      });
      throw createOfflineQueuedError({ redirectTo });
    }
    // Todo escrito; solo abortaron los refrescos de caché. Se sigue como éxito.
    logAction('ℹ️ refrescos abortados tras escribir; se sigue como éxito');
  } finally {
    deadline.clear();
  }
  logAction('✅ Proceso de guardado de nueva actuación completado con éxito');

  // Invalida informe Gestión Horaria + listas tras CREAR la actuación (mismo
  // patrón que el path de modificar/validar). Los loaders cache-first NO
  // reescriben horas/estado derivados por id → sin esto, la actuación recién
  // creada no aparece en Gestión Horaria hasta pulsar "Buscar". Se hace al
  // final, cuando ya existen action + operarios + entries (reconcile backend).
  evictDerivedCaches(client.cache);

  // El drenado de las fotos lo dispara `addImageUploadBatch` vía el evento
  // `offline-image-batch-queued`, que escucha OfflineBanner. Aquí no se espera
  // a nada: el operario ya puede seguir trabajando.

  return redirect(`../../summary/${orderActionData.actionId}`);
};
